/**
 * The proposal slideout's regions — header content and body — each a thin
 * `switch` over the sidebar's resolution state.
 *
 * `ProposalSidebar` resolves the proposal once and publishes ONE state; the
 * chrome (`SlideoutMenu`) renders the header slot — with its close button at
 * the end of the row — in every state, and these regions only decide what
 * fills the slots. A fetch that fails renders as an error the user can retry
 * or dismiss; it no longer closes the panel out from under them.
 *
 * Each switch is exhaustive: a new state member fails to compile here rather
 * than falling through silently in one region.
 */

import type { ReactElement } from 'react';

import { QueryErrorState } from '@/lib/components';
import { createSimpleContext } from '@/lib/hooks/useDataContext';

import type { ProposalDetail } from '../../lib/proposals/core/types';
import ProposalDetailHeader from './ProposalDetailHeader';
import {
  ProposalBodySkeleton,
  ProposalHeaderSkeleton,
} from './ProposalSkeleton';
import { TriggerShortcutButtons } from './TriggerShortcutButtons';
import { useTriggerShortcutButtons } from './useTriggerShortcutButtons';

/* eslint-disable react-refresh/only-export-components -- state context co-located with the regions that switch on it */

export type ProposalSidebarState =
  | { status: 'loading' }
  | { status: 'error'; error: unknown; retry: () => void }
  | { status: 'ready'; action: ProposalDetail };

export interface ProposalSidebarValue {
  state: ProposalSidebarState;
  /** The chrome's (guarded) close — the header's mobile back chevron uses it. */
  close: () => void;
}

const ProposalSidebarSurface =
  createSimpleContext<ProposalSidebarValue>('ProposalSidebar');

export const ProposalSidebarStateProvider = ProposalSidebarSurface.Provider;
export const useProposalSidebarState = ProposalSidebarSurface.use;

/**
 * Classes for the `SlideoutMenu.Header` hosting the proposal header. The
 * header content brings its own 72px row and left gutter; the right gutter
 * (`pr-5`, matching the content's `px-5`) sits on the row so the actions
 * cluster and the chrome's close end where the header's own close used to.
 * The border and background live here so they run under the close too. The
 * `md:` padding variants are zeroed/overridden explicitly — tailwind-merge
 * does not treat them as conflicting with the bare utilities.
 */
export const PROPOSAL_HEADER_CLASS =
  'p-0 pr-5 md:px-0 md:pr-5 border-b border-border-secondary bg-bg-primary';

function assertNever(state: never): never {
  throw new Error(`Unhandled proposal sidebar state: ${JSON.stringify(state)}`);
}

function ReadyHeader({ action }: { action: ProposalDetail }): ReactElement {
  const { close } = useProposalSidebarState();
  return (
    <ProposalDetailHeader
      action={action}
      onClose={close}
      bordered={false}
      variant='slideout'
    />
  );
}

/**
 * The "View X" trigger shortcut buttons, in the chrome's actions cluster so
 * they sit immediately before the close — the same row position they had
 * when the header rendered them itself. Hidden below `sm`, as before: the
 * mobile header only has room for the title stack.
 */
function ReadyHeaderActions({
  action,
}: {
  action: ProposalDetail;
}): ReactElement {
  const {
    isLoading: isLoadingTriggerButtons,
    buttons: triggerButtons,
    onActivate: activateTriggerButton,
  } = useTriggerShortcutButtons(action);
  return (
    <div className='hidden sm:flex items-center'>
      <TriggerShortcutButtons
        isLoading={isLoadingTriggerButtons}
        buttons={triggerButtons}
        orgId={action.org}
        onActivate={activateTriggerButton}
        className='flex-nowrap'
      />
    </div>
  );
}

export function ProposalSidebarHeaderActions(): ReactElement | null {
  const { state } = useProposalSidebarState();
  switch (state.status) {
    case 'ready':
      return <ReadyHeaderActions action={state.action} />;
    case 'loading':
    case 'error':
      return null;
    default:
      return assertNever(state);
  }
}

/** Header content when there is no proposal to title: the row's height, kept. */
function BareHeader(): ReactElement {
  return <div className='h-[72px]' />;
}

export function ProposalSidebarHeader(): ReactElement {
  const { state } = useProposalSidebarState();
  switch (state.status) {
    case 'ready':
      return <ReadyHeader action={state.action} />;
    case 'loading':
      return <ProposalHeaderSkeleton />;
    case 'error':
      return <BareHeader />;
    default:
      return assertNever(state);
  }
}

export function ProposalSidebarLoadingBody(): ReactElement {
  return <ProposalBodySkeleton />;
}

export function ProposalSidebarErrorBody({
  error,
  retry,
}: {
  error: unknown;
  retry: () => void;
}): ReactElement {
  return (
    <div className='p-6'>
      <QueryErrorState
        error={error}
        label="We couldn't load this action"
        onRetry={retry}
      />
    </div>
  );
}
